<template>
  <LeadHistoryCard
    :text="callResult.name"
    :user="callResult.operator"
    icon="call"
    :icon-type="iconType"
    toggle
    :expand="expand"
    :call-target-id="callTargetId"
    :call-target="callTarget"
    :history="callResult"
    :pick-up-call-target-id="pickUpCallTargetId"
    @update:pick-up-call-target-id="$emit('update:pickUpCallTargetId', $event)"
  >
    <template #date>
      <div>{{ startedAtDate }} {{ startedAtTime }} - {{ endedAtTime }}</div>
    </template>
    <template
      v-if="editable"
      #action
    >
      <div>
        <BLayout align-center>
          <BBtn
            v-if="isAvailableSalesforceApiFeature"
            class="mt-2 mr-2"
            size="small"
            fab
            flat
          >
            <BSfStatusIcon
              :connect-status="sfConnectedStatus"
              @open-sf-modal="openSfModal(sfCallResultTaskName)"
            />
          </BBtn>
          <SfStatusModal
            v-if="isAvailableSalesforceApiFeature"
            :modal-name="sfCallResultTaskName"
            :sf-related-link-info="callResult.sfRelatedLinkInfo"
            :sf-exec-sync-btn="false"
            :resync-task-info="resyncTaskInfo"
            @synced="updateSynced"
          />
          <BBtn
            v-if="callResult.miitelResultUrl"
            size="small"
            fab
            flat
          >
            <a
              class="miitel-btn"
              :href="callResult.miitelResultUrl"
              target="_blank"
              rel="noopener noreferrer"
            ><img
              class="miitel-logo"
              :src="miitelIcon"
            ></a>
          </BBtn>
          <BBtn
            size="small"
            fab
            flat
            data-test="edit"
            @click.stop="$emit('click-edit', callResult)"
          >
            <BIcon size="small">
              edit
            </BIcon>
          </BBtn>
          <BBtn
            v-if="callResult.nextActionId == null"
            size="small"
            fab
            flat
            data-test="edit"
            @click.stop="$emit('click-delete', callResult.id)"
          >
            <BIcon size="small">
              delete_forever
            </BIcon>
          </BBtn>
        </BLayout>
      </div>
    </template>
    <div class="history-card-body">
      <ZoomPhoneRecording
        :zoom-phone-recording="callResult.zoomPhoneRecording"
        :transcription="callResult.transcription"
        :call-result="callResult"
        :is-zoom-phone-connected="isZoomPhoneConnected"
        :width="500"
      />
      <template v-if="callResult.customActionValues?.length">
        <CustomActionValues
          :custom-action-values="callResult.customActionValues"
        />
        <BDivider />
      </template>
      <BEditableContentTextarea
        v-model="comment"
        class="card-item callresult-comment-area"
        :placeholder="$t('callResult.comment.text')"
        :editable="editable"
        @update:model-value="onCommentSaved"
        @click="onEditStart"
        @cancel="onEditCancel"
      />
      <p
        v-if="showSfSyncNotice && isEditing && sfConnectedStatus !== SalesforceConst.SyncedFailed"
        class="sf-sync-notice"
      >
        {{ $t('todoSync.sfSyncNotice') }}
      </p>
      <BLayout
        v-if="comment"
        justify-end
        class="mt-50"
      >
        <BCopyButton
          :text="comment"
          :title="$t('callResult.comment.text')"
          is-detailed-name
        />
      </BLayout>
    </div>
  </LeadHistoryCard>
</template>

<script>
import { mapWaitingActions } from 'vue-wait';
import miitelIcon from '@/assets/miitel.png';
import SfStatusModal from '@/components/organisms/user/general/modal/salesforce/SfStatusModal.vue';
import ZoomPhoneRecording from '@/components/organisms/user/lead/general/ZoomPhoneRecording.vue';
import CustomActionValues from '@/components/organisms/user/lead/history/CustomActionValues.vue';
import LeadHistoryCard from '@/components/organisms/user/lead/history/LeadHistoryCard.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useErrorHandler } from '@/composable/error-handler';
import { useSfTodoSyncNotice } from '@/composable/user/salesforce/use-sf-todo-sync-notice';
import SalesforceConst from '@/const/salesforce';
import BSfStatusIcon from '@/plugins/biscuet-materials/components/molecules/BSfStatusIcon.vue';
import { formatShorterDate, formatShortTime } from '@/utils/date-time';

export default {
  components: {
    LeadHistoryCard,
    ZoomPhoneRecording,
    SfStatusModal,
    BSfStatusIcon,
    CustomActionValues,
  },
  inheritAttrs: false,
  props: {
    callTargetId: {
      type: Number,
      required: true,
    },
    callResult: {
      type: Object,
      required: true,
    },
    callTarget: {
      type: Object,
      required: false,
      default: undefined,
    },
    disabled: Boolean,
    editable: {
      type: Boolean,
      default: true,
    },
    dateOnly: Boolean,
    isZoomPhoneConnected: Boolean,
    expand: {
      type: Boolean,
      default: true,
    },
    pickUpCallTargetId: {
      type: Number,
      required: false,
      default: undefined,
    },
  },
  emits: [
    'click-edit',
    'click-delete',
    'update:pickUpCallTargetId',
  ],
  setup() {
    const { isAvailableSalesforceApiFeature } = useAvailableFeatureCheck();
    const { defaultHandler } = useErrorHandler();
    const { showSfSyncNotice } = useSfTodoSyncNotice('call_result');
    return {
      defaultHandler,
      isAvailableSalesforceApiFeature,
      miitelIcon,
      showSfSyncNotice,
      SalesforceConst,
    };
  },
  data () {
    return {
      comment: this.callResult.comment,
      isEditing: false,
      resyncTaskInfo: {
        id: this.callResult.id,
        callTargetId: this.callResult.callTargetId,
        todoType: this.callResult.type,
      },
      sfConnectedStatusValue: this.callResult.sfRelatedLinkInfo?.sfRelatedLinks[0]?.status || '',
    };
  },
  computed: {
    iconType () {
      if (this.callResult.connected) {
        return 'hot';
      } else {
        return 'cold';
      }
    },
    visible () {
      return this.callResult.comment || this.callResult.miitelResultUrl || this.callResult.fieldValues.length !== 0;
    },
    startedAtDate () {
      return formatShorterDate(this.callResult.startedAt);
    },
    startedAtTime () {
      return formatShortTime(this.callResult.startedAt);
    },
    endedAtTime () {
      return formatShortTime(this.callResult.endedAt);
    },
    sfConnectedStatus() {
      return this.sfConnectedStatusValue;
    },
    sfCallResultTaskName() {
      return `sfTaskStatusCallResultModal${this.callResult.id}`;
    },
  },
  watch: {
    'callResult.comment' (newVal) {
      this.comment = newVal;
    },
  },
  methods: {
    ...mapWaitingActions('user', {
      updateCallResultAction: 'updateCallResultWait',
      getCallTargetsBackgroundAction: { action: 'getCallTargetsAction', loader: 'getCallTargetsBackgroundWait' },
      getLeadHistoriesBackgroundAction: { action: 'getLeadHistoriesAction', loader: 'getLeadHistoriesBackgroundWait' },
    }),
    ...mapWaitingActions('userUi', {
      setTargetHeaderMoveButtonDisabledAction: 'setTargetHeaderMoveButtonDisabledWait',
    }),
    openSfModal(modalName) {
      this.$modal.show(modalName);
    },
    onEditStart () {
      this.isEditing = true;
      this.setTargetHeaderMoveButtonDisable(true);
    },
    onEditCancel () {
      this.isEditing = false;
      this.setTargetHeaderMoveButtonDisable(false);
    },
    async onCommentSaved () {
      this.isEditing = false;
      await this.updateCallResult();
    },
    setTargetHeaderMoveButtonDisable (status) {
      this.setTargetHeaderMoveButtonDisabledAction(status);
    },
    async updateCallResult () {
      await this.updateCallResultAction({
        callTargetId: this.callResult.callTargetId,
        callResultId: this.callResult.id,
        body: {
          callResult: {
            ...this.callResult,
            comment: this.comment,
          },
        },
        errorHandlers: {
          422: this.defaultHandler,
        },
      });
      this.setTargetHeaderMoveButtonDisabledAction(false);
      this.getLeadHistoriesBackgroundAction({ callTargetId: this.callResult.callTargetId });
      this.getCallTargetsBackgroundAction({ callListId: this.callResult.callListId });
    },
    updateSynced() {
      this.sfConnectedStatusValue = SalesforceConst.Synced;
    },
  },
};
</script>

<style lang="scss" scoped>
.b-call-result-card {
  padding: $basespace-500 16px $basespace-500 80px
}

:deep(.zoom-phone-recording) {
  padding: $basespace-100 0;
}

.history-card-body {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.miitel-btn {
  display: flex;
  align-items: center;

  .miitel-logo {
    height: 12px;
  }
}

.callresult-comment-area {
  margin-left: -$basespace-100;
  margin-right: -$basespace-100;
}
.sf-sync-notice {
  font-size: $fontsize-100;
  color: $mars;
  margin: $basespace-100 0 $basespace-200;
}
</style>
